// src/components/Favorites/FavoritesPage.js

import React, { useState, useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import apis from "../../services/api";
import { ClipLoader } from "react-spinners";
import { ResultsContext } from "../../contexts/ResultsContext";
import { ToastContext } from "../../contexts/ToastContext";
import { mapAuthErrors } from "../../utils/errorMapping";
import ResultsDisplay from "../Search/ResultsDisplay";
import SelectedResultDisplay from "../Search/SelectedResultDisplay";
import SeasonEpisodeSelector from "../Search/SeasonEpisodeSelector";
import LinksDisplay from "../Search/LinksDisplay";
import DownloadLinksDisplay from "../Search/S1DownloadLinksDisplay";
import S2DownloadLinksDisplay from "../Search/S2DownloadLinksDisplay";
import S3DownloadLinksDisplay from "../Search/S3DownloadLinksDisplay";
import { FaStar, FaRegStar, FaExclamationCircle } from "react-icons/fa";
import { motion } from "framer-motion";
import Particles from "../Common/Particles";

export default function FavoritesPage() {
  const navigate = useNavigate(); // Initialize navigate
  const { addToast } = useContext(ToastContext);
  const { setHasResults } = useContext(ResultsContext);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoading, setFavoritesLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [links, setLinks] = useState([]);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [selectedEpisode, setSelectedEpisode] = useState(null);
  const [seasonsData, setSeasonsData] = useState([]);
  const scrollTargetRef = useRef(null);
  const seasonEpisodeSelectorRef = useRef(null);
  const favoritesToastShownRef = useRef(false);

  // Define navigateToSearch function
  const navigateToSearch = () => {
    navigate("/");
  };

  // Fetch Favorites on Component Mount
  useEffect(() => {
    const fetchFavorites = async () => {
      setFavoritesLoading(true);
      try {
        const favs = await apis.Favorites.getFavorites();
        setFavorites(favs);
        favoritesToastShownRef.current = false;
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        if (!favoritesToastShownRef.current) {
          addToast(errorMsg || "Failed to load favorites.", "error");
          favoritesToastShownRef.current = true;
        }
      } finally {
        setFavoritesLoading(false);
      }
    };

    fetchFavorites();
  }, [addToast]);

  // Function to Remove Favorite
  const handleRemoveFavorite = async (id) => {
    try {
      await apis.Favorites.removeFavorite(id);
      setFavorites((prev) => prev.filter((fav) => fav.id !== id));
      addToast(`Removed from favorites.`, "info");
    } catch (error) {
      const errorMsg = mapAuthErrors(error);
      addToast(errorMsg || "Failed to remove from favorites.", "error");
    }
  };

  // Handle Selecting a Favorite
  const handleResultClick = async (fav) => {
    setSelectedResult(fav);
    setLinks([]);
    setSelectedSeason(null);
    setSelectedEpisode(null);

    if (fav.type === "tv") {
      setLoading(true);
      try {
        const api = fav.source === "Imdb" ? apis.Imdb : apis.Tmdb;
        const identifier = fav.id;

        // Fetch Seasons and Episodes
        const data = await api.GetSeasonsAndEpisodes(identifier);
        setSeasonsData(data);
      } catch (error) {
        const errorMsg = mapAuthErrors(error);
        addToast(errorMsg || "Failed to fetch seasons and episodes.", "error");
      }
      setLoading(false);
      return;
    }

    // For Movies
    setLoading(true);
    try {
      const api = fav.source === "Imdb" ? apis.Imdb : apis.Tmdb;
      const identifier = fav.id;
      const params = { 
        [fav.source === "Imdb" ? "ImdbId" : "TmdbId"]: identifier, 
        type: "movie" 
      };

      const data = await api.getStreamingLinks(params);
      setLinks(data || []);
      if (!data || data.length === 0) {
        addToast("No links found.", "info");
      }
    } catch (error) {
      const errorMsg = mapAuthErrors(error);
      addToast(errorMsg || "An error occurred while fetching links.", "error");
    }
    setLoading(false);
  };

  // Handle Season and Episode Submission
  const handleSeasonEpisodeSubmit = async (season, episode) => {
    if (!selectedResult) return;

    setSelectedSeason(season);
    setSelectedEpisode(episode);
    setLoading(true);
    try {
      const api = selectedResult.source === "Imdb" ? apis.Imdb : apis.Tmdb;
      const identifier = selectedResult.id;
      const params = {
        [selectedResult.source === "Imdb" ? "ImdbId" : "TmdbId"]: identifier,
        type: "tv",
        season,
        episode,
      };

      const data = await api.getStreamingLinks(params);
      setLinks(data || []);
      if (!data || data.length === 0) {
        addToast("No links found.", "info");
      }
    } catch (error) {
      const errorMsg = mapAuthErrors(error);
      addToast(errorMsg || "An error occurred while fetching links.", "error");
    }
    setLoading(false);
  };

  // Scroll to Selected Result or Links Display
  useEffect(() => {
    if (
      selectedResult &&
      (links.length > 0 || (selectedResult.type === "tv" && true))
    ) {
      scrollTargetRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedResult, links]);

  // Focus on SeasonEpisodeSelector when necessary
  useEffect(() => {
    if (
      selectedResult &&
      selectedResult.type === "tv" &&
      seasonsData.length > 0 &&
      seasonEpisodeSelectorRef.current
    ) {
      seasonEpisodeSelectorRef.current.focus();
    }
  }, [selectedResult, seasonsData]);

  // Update Results Context
  useEffect(() => {
    setHasResults(selectedResult !== null || links.length > 0);
  }, [selectedResult, links, setHasResults]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-primary to-secondary text-white relative">
      <Particles count={40} />
      <main className="flex-grow relative overflow-hidden">
        <div className="flex flex-col items-center text-center py-20 px-4 text-white space-y-8 animate-zoomIn">
          {/* Header */}
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-accent flex items-center space-x-2"
            initial={{ y: -50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <FaStar className="text-yellow-400 animate-pulse" />
            <span>Your Favorites</span>
          </motion.h2>

          {/* Loading State */}
          {favoritesLoading ? (
            <motion.div
              className="flex justify-center items-center space-x-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <ClipLoader color="#ffffff" loading={favoritesLoading} size={30} />
              <span className="text-lg">Loading favorites...</span>
            </motion.div>
          ) : favorites.length === 0 ? (
            /* Empty State */
            <motion.div
              className="flex flex-col items-center space-y-4 p-6 bg-gray-800 rounded-lg shadow-lg"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <FaExclamationCircle className="text-6xl text-yellow-400" />
              <h3 className="text-2xl font-semibold">No Favorites Yet</h3>
              <p className="text-lg">Start adding your favorite movies and TV shows to see them here.</p>
              <button
                onClick={navigateToSearch} // Use the navigation function
                className="mt-4 px-6 py-2 bg-accent text-yellow-400 rounded-full hover:bg-yellow-400 hover:text-black transition-colors duration-300 flex items-center space-x-2"
              >
                <FaRegStar />
                <span>Add Favorites</span>
              </button>
            </motion.div>
          ) : (
            /* Favorites Display */
            <ResultsDisplay
              results={favorites}
              selectedResult={selectedResult}
              onResultClick={handleResultClick}
              server="" // Server is determined per favorite based on 'source'
              favorites={favorites}
              onAddFavorite={() => {}} // No adding in FavoritesPage
              onRemoveFavorite={handleRemoveFavorite}
            />
          )}

          {loading && (
            <div className="flex justify-center items-center">
              <ClipLoader color="#ffffff" loading={loading} size={50} />
            </div>
          )}

          {selectedResult && (
            <div ref={scrollTargetRef} className="w-full max-w-6xl">
              <SelectedResultDisplay
                result={selectedResult}
                selectedSeason={selectedSeason}
                selectedEpisode={selectedEpisode}
              />

              {selectedResult.type === "tv" && (
                <SeasonEpisodeSelector
                  ref={seasonEpisodeSelectorRef}
                  key={`${selectedResult.id}`}
                  onSubmit={handleSeasonEpisodeSubmit}
                  seasonsData={seasonsData}
                  server={selectedResult.source}
                  loadingSeasons={loading}
                />
              )}

              {/* Display Links Based on Source and Server */}
              {Array.isArray(links) && links.length > 0 && selectedResult.source === "S1" && (
                <DownloadLinksDisplay links={links} />
              )}

              {links && links.contentType && selectedResult.source === "S2" && (
                <S2DownloadLinksDisplay links={links} selectedResult={selectedResult} />
              )}

              {Array.isArray(links) && links.length > 0 && selectedResult.source === "S3" && (
                <S3DownloadLinksDisplay links={links} selectedResult={selectedResult} />
              )}

              {Array.isArray(links) &&
                links.length > 0 &&
                selectedResult.source !== "S1" &&
                selectedResult.source !== "S2" &&
                selectedResult.source !== "S3" && <LinksDisplay links={links} />}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
